"use client";

import * as React from "react";

const KEY = "jobutu.theme";

export function ThemeToggle() {
  const [theme, setTheme] = React.useState<"light" | "dark">("dark");

  React.useEffect(() => {
    const stored =
      typeof window !== "undefined"
        ? (window.localStorage.getItem(KEY) as "light" | "dark" | null)
        : null;
    const initial =
      stored ??
      (typeof window !== "undefined" &&
      window.matchMedia("(prefers-color-scheme: light)").matches
        ? "light"
        : "dark");
    setTheme(initial);
    apply(initial);
  }, []);

  const apply = React.useCallback((t: "light" | "dark") => {
    if (typeof document === "undefined") return;
    document.body.setAttribute("data-lk-theme", t === "light" ? "light" : "default");
    document.documentElement.setAttribute("data-jobutu-theme", t);
  }, []);

  const toggle = React.useCallback(() => {
    const next = theme === "dark" ? "light" : "dark";
    setTheme(next);
    apply(next);
    try {
      window.localStorage.setItem(KEY, next);
    } catch {
      /* noop */
    }
  }, [theme, apply]);

  return (
    <button
      type="button"
      onClick={toggle}
      aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
      title={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
      className="theme-toggle"
    >
      <span className={`theme-toggle-icon ${theme}`} aria-hidden>
        {theme === "dark" ? (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <path
              d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="currentColor"
              fillOpacity="0.15"
            />
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="4" fill="currentColor" />
            <g stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line x1="12" y1="2" x2="12" y2="4" />
              <line x1="12" y1="20" x2="12" y2="22" />
              <line x1="2" y1="12" x2="4" y2="12" />
              <line x1="20" y1="12" x2="22" y2="12" />
              <line x1="4.93" y1="4.93" x2="6.34" y2="6.34" />
              <line x1="17.66" y1="17.66" x2="19.07" y2="19.07" />
              <line x1="4.93" y1="19.07" x2="6.34" y2="17.66" />
              <line x1="17.66" y1="6.34" x2="19.07" y2="4.93" />
            </g>
          </svg>
        )}
      </span>
      <style>{`
        .theme-toggle {
          width: 36px; height: 36px;
          border: 1px solid rgba(255,255,255,0.12);
          background: rgba(255,255,255,0.06);
          color: #e8ecff;
          border-radius: 999px;
          display: grid; place-items: center;
          cursor: pointer;
          transition: background 150ms, transform 200ms cubic-bezier(.2,1.6,.6,1), border-color 150ms;
        }
        .theme-toggle:hover { background: rgba(255,255,255,0.1); transform: scale(1.05); }
        .theme-toggle:active { transform: scale(0.96); }
        .theme-toggle-icon {
          display: inline-flex;
          transition: transform 320ms cubic-bezier(.4,1.4,.6,1), color 200ms;
        }
        .theme-toggle-icon.dark { color: #b8c4ff; }
        .theme-toggle-icon.light { color: #f7c948; transform: rotate(180deg); }
      `}</style>
    </button>
  );
}
